import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ClientTable from './components/ClientTable';
import ClientFilters from './components/ClientFilters';
import BulkActions from './components/BulkActions';
import ClientStats from './components/ClientStats';
import ServicePieChart from './components/ServicePieChart';

// API base URL - adjust this if needed
const API_BASE_URL = 
  window.location.hostname === "localhost"
    ? "http://localhost:5454"
    : "https://studio-musu-photography.onrender.com";


const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    serviceType: '',
    status: '',
    location: '',
    dateRange: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch clients from database
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/api/bookings`, {
          headers: {
            'Accept': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const bookingsData = result.bookings || [];

        if (!Array.isArray(bookingsData)) {
          throw new Error('Expected array of bookings but received different data structure');
        }

        const transformedClients = bookingsData.map(booking => ({
          id: booking._id || booking.id,
          name: booking.name || 'Unknown Client',
          email: booking.email || '',
          phone: booking.phone || '',
          location: booking.location || '',
          inquiryDate: booking.createdAt || booking.date || new Date().toISOString(),
          lastInteraction: booking.updatedAt || booking.createdAt || new Date().toISOString(),
          serviceInterest: booking.service || 'Unknown Service',
          status: 'new', // Default status since your DB doesn't have status field
          budgetRange: booking.total ? `₹${booking.total}` : '₹0',
          message: `Service: ${booking.service || 'N/A'}, Duration: ${booking.duration || 'N/A'}, Date: ${booking.date || 'N/A'}, Time: ${booking.time || 'N/A'}`,
          notes: [],
          date: booking.date,
          time: booking.time,
          duration: booking.duration,
          total: booking.total
        }));

        setClients(transformedClients);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Calculate service distribution for pie chart
  const serviceDistribution = useMemo(() => {
    const distribution = {};
    clients.forEach(client => {
      const service = client.serviceInterest;
      distribution[service] = (distribution[service] || 0) + 1;
    });
    return distribution;
  }, [clients]);

  // Filter clients based on current filters
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = !filters.search ||
        client.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        client.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        (client.phone && client.phone.includes(filters.search));

      const matchesService = !filters.serviceType ||
        client.serviceInterest === filters.serviceType;

      const matchesStatus = !filters.status ||
        client.status === filters.status;

      const matchesLocation = !filters.location ||
        (client.location && client.location.includes(filters.location));

      const matchesDateRange = !filters.dateRange || (() => {
        try {
          const clientDate = new Date(client.inquiryDate);
          const now = new Date();

          switch (filters.dateRange) {
            case 'today':
              return clientDate.toDateString() === now.toDateString();
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return clientDate >= weekAgo;
            case 'month':
              return clientDate.getMonth() === now.getMonth() &&
                clientDate.getFullYear() === now.getFullYear();
            case 'quarter':
              const quarter = Math.floor(now.getMonth() / 3);
              const clientQuarter = Math.floor(clientDate.getMonth() / 3);
              return clientQuarter === quarter &&
                clientDate.getFullYear() === now.getFullYear();
            case 'year':
              return clientDate.getFullYear() === now.getFullYear();
            default:
              return true;
          }
        } catch (e) {
          return true;
        }
      })();

      return matchesSearch && matchesService && matchesStatus &&
        matchesLocation && matchesDateRange;
    });
  }, [clients, filters]);

  // Handle client selection
  const handleClientSelect = (clientId, isSelected) => {
    if (isSelected) {
      setSelectedClients(prev => [...prev, clientId]);
    } else {
      setSelectedClients(prev => prev.filter(id => id !== clientId));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedClients(filteredClients.map(client => client._id));
    } else {
      setSelectedClients([]);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (clientId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/${clientId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setClients(prev => prev.map(client =>
          client.id === clientId ? { ...client, status: newStatus } : client
        ));
      }
    } catch (err) {
      // Optionally handle error
    }
  };

  // Handle send email
  const handleSendEmail = (client) => {
    // Implement as needed
  };

  // Handle schedule callback
  const handleScheduleCallback = (client) => {
    // Implement as needed
  };

  // Handle bulk actions
  const handleBulkAction = (actionId, clientIds) => {
    setSelectedClients([]);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedClients([]);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSelectedClients([]);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      search: '',
      serviceType: '',
      status: '',
      location: '',
      dateRange: ''
    });
  };

  // Convert data to CSV format
  const convertToCSV = (data) => {
    const headers = [
      'ID',
      'Name',
      'Email',
      'Phone',
      'Location',
      'Inquiry Date',
      'Service',
      'Date',
      'Time',
      'Duration',
      'Total',
      'Message'
    ];

    const rows = data.map(client => [
      client.id,
      `"${client.name}"`,
      client.email,
      client.phone,
      `"${client.location}"`,
      new Date(client.inquiryDate).toLocaleDateString(),
      client.serviceInterest,
      client.date,
      client.time,
      client.duration,
      `₹${client.total}`,
      `"${client.message.replace(/"/g, '""')}"`
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  };

  // Download CSV file
  const downloadCSV = (data, filename = 'clients_export.csv') => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle export data
  const handleExportData = () => {
    const dataToExport = selectedClients.length > 0
      ? filteredClients.filter(client => selectedClients.includes(client.id))
      : filteredClients;

    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    const filename = selectedClients.length > 0
      ? `selected_clients_${new Date().toISOString().slice(0, 10)}.csv`
      : `all_clients_${new Date().toISOString().slice(0, 10)}.csv`;

    downloadCSV(dataToExport, filename);
  };

  // --- Handle delete client ---
  // ...existing code...

  const handleDeleteClient = async (clientId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/${clientId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete client');
      }
      setClients(prev => prev.filter(client => client.id !== clientId));
      setSelectedClients(prev => prev.filter(id => id !== clientId));
    } catch (err) {
      alert('Failed to delete client. Please try again.');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-secondary mt-20 mb-20 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading clients...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-secondary mt-20 mb-20 flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Error loading clients</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {/* Make sure your backend is running on http://localhost:5454 */}
            </p>
            <Button
              variant="default"
              onClick={() => window.location.reload()}
              className="mr-2"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary mt-20 mb-20">
      {/* Page Header */}
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Link
                  to="/service-booking"
                  className="text-muted-foreground hover:text-accent transition-smooth"
                >
                  <Icon name="ArrowLeft" size={20} />
                </Link>
                <h1 className="text-3xl font-heading font-bold text-primary">Client Management</h1>
              </div>
              <p className="text-muted-foreground font-body">
                Manage {clients.length} client bookings and inquiries
              </p>
            </div>




            <div className="flex items-center space-x-3">
                          <Button
  onClick={() => {
    localStorage.removeItem("isAdmin");
    window.location.href = "/login"; // redirect to login
  }}
   className="px-4 py-2 rounded-lg text-white font-medium 
             bg-gradient-to-r from-purple-600 to-red-400 
             hover:from-blue-600 hover:to-red-500 
             transition-all shadow-md"
>
  🔒 Logout
</Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportData}
                disabled={filteredClients.length === 0}
              >
                {selectedClients.length > 0 ? `Export (${selectedClients.length})` : 'Export All'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Client Statistics */}
        <ClientStats clients={filteredClients} />

        {/* Service Distribution Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6">
          <ServicePieChart data={serviceDistribution} />
        </div>

        {/* Filters */}
        <ClientFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onExportData={handleExportData}
          totalClients={clients.length}
          filteredCount={filteredClients.length}
        />

        {/* Bulk Actions */}
        {selectedClients.length > 0 && (
          <BulkActions
            selectedClients={selectedClients}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
          />
        )}

        {/* Client Table */}
        {filteredClients.length > 0 ? (
          <ClientTable
            clients={filteredClients}
            onStatusUpdate={handleStatusUpdate}
            onSendEmail={handleSendEmail}
            onScheduleCallback={handleScheduleCallback}
            onBulkAction={handleBulkAction}
            selectedClients={selectedClients}
            onClientSelect={handleClientSelect}
            onSelectAll={handleSelectAll}
            onDeleteClient={handleDeleteClient} // <-- Pass the delete handler here
          />
        ) : (
          <div className="bg-card rounded-lg border border-border p-12 text-center">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h3 className="text-lg font-heading font-medium text-primary mb-2">
              {clients.length === 0 ? 'No clients found' : 'No matching clients'}
            </h3>
            <p className="text-muted-foreground font-body mb-6">
              {clients.length === 0
                ? 'No client bookings found in the system.'
                : 'Try adjusting your filters to see more results.'
              }
            </p>
            {clients.length === 0 ? (
              <Link to="/service-booking">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create First Booking
                </Button>
              </Link>
            ) : (
              <Button
                variant="outline"
                iconName="X"
                iconPosition="left"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;